import { useEffect, useState } from 'react';
import type { LegalReqType, LegalRequirementPayload } from './types';
import { LEGAL_REQ_TYPE_LABEL } from './legalRequirementsUtils';

interface LegalRequirementFormModalProps {
  open: boolean;
  initial?: Partial<LegalRequirementPayload>;
  title: string;
  onClose: () => void;
  onSubmit: (payload: LegalRequirementPayload) => Promise<void>;
}

const TYPES = Object.keys(LEGAL_REQ_TYPE_LABEL) as LegalReqType[];

export function LegalRequirementFormModal({
  open,
  initial,
  title,
  onClose,
  onSubmit,
}: LegalRequirementFormModalProps) {
  const [reqTitle, setReqTitle] = useState('');
  const [type, setType] = useState<LegalReqType>('ley');
  const [issuingBody, setIssuingBody] = useState('');
  const [reference, setReference] = useState('');
  const [scope, setScope] = useState('');
  const [processArea, setProcessArea] = useState('');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [alertEnabled, setAlertEnabled] = useState(false);
  const [alertDaysBefore, setAlertDaysBefore] = useState('30');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setReqTitle(initial?.title ?? '');
    setType(initial?.type ?? 'ley');
    setIssuingBody(initial?.issuingBody ?? '');
    setReference(initial?.reference ?? '');
    setScope(initial?.scope ?? '');
    setProcessArea(initial?.processArea ?? '');
    setEffectiveDate(initial?.effectiveDate?.slice(0, 10) ?? '');
    setExpiryDate(initial?.expiryDate?.slice(0, 10) ?? '');
    setAlertEnabled(initial?.alertEnabled ?? false);
    setAlertDaysBefore(String(initial?.alertDaysBefore ?? 30));
  }, [open, initial]);

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reqTitle.trim() || !scope.trim()) return;
    if (alertEnabled && !alertDaysBefore) return;
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit({
        title: reqTitle.trim(),
        type,
        issuingBody: issuingBody.trim() || null,
        reference: reference.trim() || null,
        scope: scope.trim(),
        processArea: processArea.trim() || null,
        effectiveDate: effectiveDate ? new Date(effectiveDate).toISOString() : null,
        expiryDate: expiryDate ? new Date(expiryDate).toISOString() : null,
        alertEnabled,
        alertDaysBefore: alertEnabled ? Number.parseInt(alertDaysBefore, 10) : null,
      });
      onClose();
    } catch {
      setError('No se pudo guardar el requisito legal.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 space-y-4">
        <h2 className="text-lg font-semibold">{title}</h2>
        <form className="space-y-3" onSubmit={(e) => void handleSubmit(e)}>
          <label className="block text-sm">
            Título
            <input
              className="mt-1 w-full rounded-md border border-border px-3 py-2 text-sm"
              value={reqTitle}
              onChange={(e) => setReqTitle(e.target.value)}
              required
              placeholder="Ej: Ley de protección de datos"
            />
          </label>
          <label className="block text-sm">
            Tipo
            <select
              className="mt-1 w-full rounded-md border border-border px-3 py-2 text-sm"
              value={type}
              onChange={(e) => setType(e.target.value as LegalReqType)}
            >
              {TYPES.map((t) => (
                <option key={t} value={t}>
                  {LEGAL_REQ_TYPE_LABEL[t]}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm">
            Organismo emisor
            <input
              className="mt-1 w-full rounded-md border border-border px-3 py-2 text-sm"
              value={issuingBody}
              onChange={(e) => setIssuingBody(e.target.value)}
              placeholder="Ej: Ministerio de Trabajo"
            />
          </label>
          <label className="block text-sm">
            Referencia
            <input
              className="mt-1 w-full rounded-md border border-border px-3 py-2 text-sm"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="Ej: Ley 24.240, art. 4"
            />
          </label>
          <label className="block text-sm">
            Alcance / aplicabilidad
            <textarea
              className="mt-1 w-full rounded-md border border-border px-3 py-2 text-sm min-h-[80px]"
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              required
              placeholder="Indique a qué procesos o áreas aplica…"
            />
          </label>
          <label className="block text-sm">
            Área de proceso
            <input
              className="mt-1 w-full rounded-md border border-border px-3 py-2 text-sm"
              value={processArea}
              onChange={(e) => setProcessArea(e.target.value)}
              placeholder="Ej: RRHH, Calidad…"
            />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-sm">
              Vigencia desde
              <input
                type="date"
                className="mt-1 w-full rounded-md border border-border px-3 py-2 text-sm"
                value={effectiveDate}
                onChange={(e) => setEffectiveDate(e.target.value)}
              />
            </label>
            <label className="block text-sm">
              Vencimiento
              <input
                type="date"
                className="mt-1 w-full rounded-md border border-border px-3 py-2 text-sm"
                value={expiryDate}
                onChange={(e) => setExpiryDate(e.target.value)}
              />
            </label>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={alertEnabled}
              onChange={(e) => setAlertEnabled(e.target.checked)}
            />
            Alertar antes del vencimiento
          </label>
          {alertEnabled && (
            <label className="block text-sm">
              Días de anticipación (1–365)
              <input
                type="number"
                min={1}
                max={365}
                className="mt-1 w-full rounded-md border border-border px-3 py-2 text-sm"
                value={alertDaysBefore}
                onChange={(e) => setAlertDaysBefore(e.target.value)}
                required
                placeholder="Ej: 30"
              />
            </label>
          )}
          {error && <p className="text-sm text-destructive">{error}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className="btn btn-outline" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
